import {useEffect, useRef, useState} from 'react';
import {advertisersApi, promptApi} from '../../services/api';
import {BrandSafetyPrompt, Company, EskimiAdvertiser} from '../../types';
import {useAllCategoriesExcluded} from '../CategoryExclusionSelect';
import {DEFAULT_EXCLUDED_CATEGORIES} from '../../constants/dimensionLabels';
import {setSelectedBlueprintId} from '../../hooks/useSelectedBlueprint';
import {BlueprintFormData, MatchInsights} from './types';

const EMPTY_FORM_DATA: BlueprintFormData = {
    name: '',
    description: '',
    prompt: '',
    advertiserId: null,
    countryCodes: [],
    excludedCategories: DEFAULT_EXCLUDED_CATEGORIES as number[],
};

// Unsaved-draft persistence (bd deepcontext-5ime.2): a forced /login redirect
// (session expiry) or a full-page reload remounts this hook from scratch,
// discarding any in-progress edit held only in useState. Mirrors the
// try/catch-guarded pattern in hooks/useSelectedBlueprint.ts and the
// relevance-engine persist hook — sessionStorage only (per-tab, cleared on
// tab close), keyed per blueprint id ('new' for the create modal).
const DRAFT_STORAGE_PREFIX = 'eskimi:blueprintDraft:';
const DRAFT_DEBOUNCE_MS = 400;

function draftKeyFor(blueprintId: string | null): string {
    return `${DRAFT_STORAGE_PREFIX}${blueprintId || 'new'}`;
}

function readDraft(blueprintId: string | null): BlueprintFormData | null {
    try {
        const raw = sessionStorage.getItem(draftKeyFor(blueprintId));
        return raw ? (JSON.parse(raw) as BlueprintFormData) : null;
    } catch {
        return null;
    }
}

function writeDraft(blueprintId: string | null, data: BlueprintFormData): void {
    try {
        sessionStorage.setItem(draftKeyFor(blueprintId), JSON.stringify(data));
    } catch {
        // ignore — storage unavailable
    }
}

function clearDraft(blueprintId: string | null): void {
    try {
        sessionStorage.removeItem(draftKeyFor(blueprintId));
    } catch {
        // ignore — storage unavailable
    }
}

interface FormUser {
    id: string;
    role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
}

/**
 * Owns the create/edit Blueprint modal: form state, SSP sync toggles, AI
 * rewrite, match-insights advisory, and the advertiser search. Extracted
 * from PromptManager.tsx (deepcontext-ityf decomposition) — behavior
 * unchanged.
 */
export function useBlueprintForm(
    user: FormUser | null | undefined,
    selectedCompanyId: string | null | undefined,
    companies: Company[],
    reload: () => Promise<void>,
    setError: (msg: string | null) => void,
) {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<BrandSafetyPrompt | null>(null);
    const [formData, setFormData] = useState<BlueprintFormData>(EMPTY_FORM_DATA);
    const allCategoriesExcluded = useAllCategoriesExcluded(formData.excludedCategories);
    const [syncToIndex, setSyncToIndex] = useState(false);
    const [syncToEquativ, setSyncToEquativ] = useState(false);
    const [syncToEquativPublic, setSyncToEquativPublic] = useState(false);
    const [syncToPubmatic, setSyncToPubmatic] = useState(false);
    const [isRewriting, setIsRewriting] = useState(false);
    const [showAIRewrite, setShowAIRewrite] = useState(false);
    // Generate with AI (bd deepcontext-0445.2): Write / Generate mode switch in
    // the content section. Brand URL is a generate-time input only — it is
    // never persisted alongside the prompt.
    const [contentMode, setContentMode] = useState<'write' | 'generate'>('write');
    const [brandUrl, setBrandUrl] = useState('');
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
    const [isLoadingModal, setIsLoadingModal] = useState(false);
    // deepcontext-8ew4 — surfaces when the full-prompt fetch (with content)
    // fails, instead of silently rendering the content-less list-row fallback.
    const [contentLoadError, setContentLoadError] = useState<string | null>(null);
    // deepcontext-33p5.2 — errors raised while the modal is open (validation,
    // save, AI rewrite/generate) render inside the modal; the page-level
    // setError banner sits behind it and is kept for list/delete failures.
    const [formError, setFormError] = useState<string | null>(null);
    const [rewriteResult, setRewriteResult] = useState<{
        rewrittenPrompt: string;
        originalPrompt: string;
    } | null>(null);
    // Match-insights advisory (bd bqw.9 + bqw.17). On-demand only — no caching,
    // each click re-samples and re-calls the LLM. Response now includes a
    // structured theme-count breakdown alongside the narrative.
    const [isLoadingMatchInsights, setIsLoadingMatchInsights] = useState(false);
    const [matchInsights, setMatchInsights] = useState<MatchInsights | null>(null);

    // Draft persistence — see DRAFT_STORAGE_PREFIX above. Tracks which key the
    // debounced write below should target ('new' while creating, the
    // blueprint id while editing); a ref so the debounce timer always reads
    // the current value without needing to be in its own effect dependency.
    const draftIdRef = useRef<string | null>(null);

    // While handleEdit's fetch is in flight, formData still holds the
    // previous (stale/empty) value — writing it here would race the fetch:
    // if it resolves after DRAFT_DEBOUNCE_MS, this stale write lands after
    // handleEdit's own read, clobbering a real draft with blank data and
    // making handleEdit read back its own clobber instead of `null`. Gate on
    // `!isLoadingModal` so nothing is written until formData reflects the
    // blueprint draftIdRef.current actually points at.
    const isModalOpen = (showCreateModal || !!editingPrompt) && !isLoadingModal;
    useEffect(() => {
        if (!isModalOpen) return;
        const timer = setTimeout(() => {
            writeDraft(draftIdRef.current, formData);
        }, DRAFT_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [formData, isModalOpen]);

    const handleGetMatchInsights = async () => {
        if (!editingPrompt?.id) return;
        setIsLoadingMatchInsights(true);
        setMatchInsights(null);
        try {
            const result = await promptApi.getMatchInsights(editingPrompt.id);
            setMatchInsights(result);
        } catch (err: any) {
            setMatchInsights({
                status: 'error',
                narrative: null,
                error: err?.response?.data?.error || err?.message || 'Failed to get match insights',
            });
        } finally {
            setIsLoadingMatchInsights(false);
        }
    };

    // Advertiser search state
    const [advertisers, setAdvertisers] = useState<EskimiAdvertiser[]>([]);
    const [advertiserSearchTerm, setAdvertiserSearchTerm] = useState('');
    const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);

    const handleCreate = () => {
        setShowCreateModal(true);
        setEditingPrompt(null);
        draftIdRef.current = null;
        setFormData(readDraft(null) ?? EMPTY_FORM_DATA);
        setSyncToIndex(false);
        setSyncToEquativ(false);
        setSyncToEquativPublic(false);
        setSyncToPubmatic(false);
        setAdvertiserSearchTerm('');
        setAdvertisers([]);
        setContentLoadError(null);
        setFormError(null);
        setContentMode('write');
        setBrandUrl('');
    };

    const handleEdit = async (prompt: BrandSafetyPrompt) => {
        // Set editing prompt first and show loading state
        setEditingPrompt(prompt);
        draftIdRef.current = prompt.id;
        setIsLoadingModal(true);
        setContentLoadError(null);
        setFormError(null);
        setContentMode('write');
        setBrandUrl('');

        // Run all async checks in parallel, including fetching full prompt content
        const [fullPromptResult, indexResult, equativResult, pubmaticResult, advertiserResult] = await Promise.allSettled([
            // Fetch full prompt with content (prompt_text is not included in list API
            // for performance). companyId lets staff read blueprints of the selected
            // company that they did not create; the creator can always read their own
            // (canUserReadPrompt) — deepcontext-8ew4, deepcontext-b0op.
            selectedCompanyId ? promptApi.getById(prompt.id, selectedCompanyId) : promptApi.getById(prompt.id),
            // Check Index Exchange segment
            promptApi.checkIndexSegment(prompt.id).catch(() => ({exists: false})),
            // Check Equativ segment
            promptApi.checkEquativSegment(prompt.id).catch(() => ({exists: false, publicExists: false})),
            // Check PubMatic segment
            promptApi.checkPubmaticSegment(prompt.id).catch(() => ({exists: false})),
            // Fetch advertiser info if needed
            (async () => {
                if (prompt.companyId) {
                    const company = companies.find(c => c.id === prompt.companyId);
                    if (company?.advertiserId) {
                        try {
                            const advertiser = await advertisersApi.getById(company.advertiserId);
                            return {id: company.advertiserId, name: advertiser.name};
                        } catch {
                            return {id: company.advertiserId, name: ''};
                        }
                    }
                }
                return null;
            })()
        ]);

        // Get full prompt data (with prompt content). On failure, surface an
        // error instead of silently rendering the content-less list row as if
        // it were complete — deepcontext-8ew4.
        const fullPrompt = fullPromptResult.status === 'fulfilled' ? fullPromptResult.value : prompt;
        if (fullPromptResult.status === 'rejected') {
            setContentLoadError('Failed to load blueprint content. Please close and try again.');
        }

        // Process Index result
        if (indexResult.status === 'fulfilled') {
            setSyncToIndex(indexResult.value.exists);
        } else {
            setSyncToIndex(false);
        }

        // Process Equativ result
        if (equativResult.status === 'fulfilled') {
            setSyncToEquativ(equativResult.value.exists);
            setSyncToEquativPublic(equativResult.value.publicExists === true);
        } else {
            setSyncToEquativ(false);
            setSyncToEquativPublic(false);
        }

        // Process PubMatic result
        if (pubmaticResult.status === 'fulfilled') {
            setSyncToPubmatic(pubmaticResult.value.exists);
        } else {
            setSyncToPubmatic(false);
        }

        // Process advertiser result
        let advertiserId = null;
        let advertiserName = '';
        if (advertiserResult.status === 'fulfilled' && advertiserResult.value) {
            advertiserId = advertiserResult.value.id;
            advertiserName = advertiserResult.value.name;
        }

        // Parse countries from comma-separated string to array
        const countryCodes = fullPrompt.countries ? fullPrompt.countries.split(',').filter(c => c.trim()) : [];

        const fetchedFormData: BlueprintFormData = {
            name: fullPrompt.name,
            description: fullPrompt.description,
            prompt: fullPrompt.prompt || '',
            advertiserId: advertiserId,
            countryCodes: countryCodes,
            excludedCategories: fullPrompt.excludedCategories ?? [],
        };
        setFormData(readDraft(prompt.id) ?? fetchedFormData);
        setAdvertiserSearchTerm(advertiserName);
        setAdvertisers([]);
        setIsLoadingModal(false);
    };

    const handleSave = async () => {
        setFormError(null);
        if (!formData.name || !formData.prompt) {
            setFormError('Name and prompt are required');
            return;
        }
        if (allCategoriesExcluded) {
            setFormError('Cannot save: all URL categories are excluded. This blueprint would match zero URLs.');
            return;
        }

        try {
            // Convert country codes array to comma-separated lowercase string
            const countriesString = formData.countryCodes.length > 0
                ? formData.countryCodes.map(c => c.toLowerCase()).join(',')
                : undefined;

            const dataToSend: any = {
                name: formData.name,
                description: formData.description,
                prompt: formData.prompt,
                type: 'brand-safety' as const,
                advertiserId: formData.advertiserId || undefined,
                syncToIndex: syncToIndex,
                syncToEquativ: syncToEquativ,
                syncToEquativPublic: syncToEquativPublic,
                syncToPubmatic: syncToPubmatic,
                countries: countriesString,
                excludedCategories: formData.excludedCategories.length > 0 ? formData.excludedCategories : null,
            };

            // For eskimi_admin and adops users creating a new blueprint, include the selected company ID
            if (!editingPrompt && (user?.role === 'eskimi_admin' || user?.role === 'adops') && selectedCompanyId) {
                dataToSend.companyId = selectedCompanyId;
            }

            if (editingPrompt) {
                await promptApi.update(editingPrompt.id, dataToSend);
            } else {
                const created = await promptApi.create(dataToSend);
                setSelectedBlueprintId(created.id);
            }

            clearDraft(draftIdRef.current);
            draftIdRef.current = null;
            setShowCreateModal(false);
            setEditingPrompt(null);
            setFormData(EMPTY_FORM_DATA);
            setSyncToIndex(false);
            setSyncToEquativ(false);
            setSyncToEquativPublic(false);
            setSyncToPubmatic(false);
            // Reload with the active filters (bd-sc1z) — a bare reload of the
            // unfiltered defaults would reset the table instead of preserving
            // what the user had set.
            await reload();
        } catch (err: any) {
            setFormError(err.response?.data?.error || 'Failed to save prompt');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this prompt?')) {
            return;
        }

        try {
            await promptApi.delete(id);
            await reload();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to delete prompt');
        }
    };

    const closeModal = () => {
        clearDraft(draftIdRef.current);
        draftIdRef.current = null;
        setShowCreateModal(false);
        setEditingPrompt(null);
        setFormData(EMPTY_FORM_DATA);
        setSyncToIndex(false);
        setSyncToEquativ(false);
        setSyncToEquativPublic(false);
        setSyncToPubmatic(false);
        setShowAIRewrite(false);
        setRewriteResult(null);
        setAdvertiserSearchTerm('');
        setAdvertisers([]);
        setIsLoadingModal(false);
        setMatchInsights(null);
        setIsLoadingMatchInsights(false);
        setContentLoadError(null);
        setFormError(null);
        setContentMode('write');
        setBrandUrl('');
    };

    const handleRewriteWithAI = async () => {
        if (!formData.prompt?.trim()) {
            setFormError('Please enter some prompt content to rewrite');
            return;
        }

        setIsRewriting(true);
        setFormError(null);

        try {
            const result = await promptApi.rewriteWithAI(formData.prompt);
            setRewriteResult(result);
            setShowAIRewrite(true);
        } catch (err: any) {
            setFormError(err.response?.data?.error || 'Failed to rewrite prompt with AI');
        } finally {
            setIsRewriting(false);
        }
    };

    // Generate with AI (bd deepcontext-0445.2): creates a brand-new prompt from
    // the brand's own site content + the selected countries, instead of
    // editing existing text. Reuses the same accept/reject review modal as
    // Enhance — the result lands in `rewriteResult` either way.
    const handleGenerateWithAI = async () => {
        if (!brandUrl.trim()) {
            setFormError('Please enter a brand URL to generate from');
            return;
        }
        if (formData.countryCodes.length === 0) {
            setFormError('Select at least one country to generate');
            return;
        }

        setIsGeneratingPrompt(true);
        setFormError(null);

        try {
            const result = await promptApi.generateWithAI(brandUrl.trim(), formData.countryCodes);
            setRewriteResult({rewrittenPrompt: result.generatedPrompt, originalPrompt: formData.prompt});
            setShowAIRewrite(true);
        } catch (err: any) {
            setFormError(err.response?.data?.error || 'Failed to generate prompt with AI');
        } finally {
            setIsGeneratingPrompt(false);
        }
    };

    const handleAcceptAIRewrite = () => {
        if (rewriteResult) {
            setFormData({...formData, prompt: rewriteResult.rewrittenPrompt});
            setShowAIRewrite(false);
            setRewriteResult(null);
            // Accepted text always lands as editable/enhanceable content —
            // switch back to Write mode even if this came from Generate.
            setContentMode('write');
        }
    };

    const handleRejectAIRewrite = () => {
        setShowAIRewrite(false);
        setRewriteResult(null);
    };

    const selectAdvertiser = (advertiser: EskimiAdvertiser) => {
        setFormData({...formData, advertiserId: advertiser.id});
        setAdvertiserSearchTerm(advertiser.name);
        setAdvertisers([]);
    };

    const clearAdvertiser = () => {
        setFormData({...formData, advertiserId: null});
        setAdvertiserSearchTerm('');
    };

    // Search advertisers with debouncing
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (advertiserSearchTerm.trim()) {
                searchAdvertisers(advertiserSearchTerm);
            } else {
                setAdvertisers([]);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertiserSearchTerm]);

    const searchAdvertisers = async (searchTerm: string) => {
        setLoadingAdvertisers(true);
        try {
            const response = await advertisersApi.search(searchTerm);
            setAdvertisers(response);
        } catch (err) {
            console.error('Failed to search advertisers:', err);
            setAdvertisers([]);
        } finally {
            setLoadingAdvertisers(false);
        }
    };

    return {
        showCreateModal,
        editingPrompt,
        formData,
        setFormData,
        allCategoriesExcluded,
        syncToIndex,
        setSyncToIndex,
        syncToEquativ,
        setSyncToEquativ,
        syncToEquativPublic,
        setSyncToEquativPublic,
        syncToPubmatic,
        setSyncToPubmatic,
        isRewriting,
        showAIRewrite,
        isLoadingModal,
        contentLoadError,
        formError,
        rewriteResult,
        contentMode,
        setContentMode,
        brandUrl,
        setBrandUrl,
        isGeneratingPrompt,
        handleGenerateWithAI,
        isLoadingMatchInsights,
        matchInsights,
        handleGetMatchInsights,
        advertisers,
        advertiserSearchTerm,
        setAdvertiserSearchTerm,
        loadingAdvertisers,
        handleCreate,
        handleEdit,
        handleSave,
        handleDelete,
        closeModal,
        handleRewriteWithAI,
        handleAcceptAIRewrite,
        handleRejectAIRewrite,
        selectAdvertiser,
        clearAdvertiser,
    };
}

export type UseBlueprintFormResult = ReturnType<typeof useBlueprintForm>;
